import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Calendar as CalIcon } from "lucide-react";

const MONTH_NAMES = ["enero","febrero","marzo","abril","mayo","junio",
                     "julio","agosto","septiembre","octubre","noviembre","diciembre"];
const WEEKDAYS = ["L","M","X","J","V","S","D"];

// Distinct palette, applied by chronological index (adjacent tours never share color).
const TOUR_COLORS = ["#026FCB","#10B981","#F97316","#8B5CF6","#EF4444","#0EA5E9","#F59E0B","#84CC16","#EC4899","#6366F1"];

function iso(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function buildMonth(year, month, tours) {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // Monday=0 ... Sunday=6
  const jsDay = first.getDay(); // 0=Sun..6=Sat
  const startPadding = (jsDay + 6) % 7;
  const cells = [];
  for (let i = 0; i < startPadding; i++) cells.push({ empty: true });
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = iso(year, month, d);
    const inTours = tours.filter((t) => t.start_date && t.end_date &&
                                        t.start_date <= dateStr && dateStr <= t.end_date);
    cells.push({ day: d, dateStr, inTours });
  }
  while (cells.length % 7 !== 0) cells.push({ empty: true });
  return cells;
}

function MonthGrid({ year, month, tours, colorFor }) {
  const cells = useMemo(() => buildMonth(year, month, tours), [year, month, tours]);
  const todayISO = new Date().toISOString().slice(0, 10);
  return (
    <div>
      <div className="text-sm font-semibold capitalize mb-2 text-slate-700 dark:text-slate-200">
        {MONTH_NAMES[month]} {year}
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-[10px] mono uppercase text-slate-400 mb-1">
        {WEEKDAYS.map((w) => <div key={w} className="text-center">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((c, i) => {
          if (c.empty) return <div key={i} className="aspect-square" />;
          const hit = (c.inTours || []).length > 0;
          const isToday = c.dateStr === todayISO;
          const first = c.inTours?.[0];
          return (
            <Link
              key={i}
              to={first ? `/tours/${first.id}` : "#"}
              onClick={(e) => { if (!first) e.preventDefault(); }}
              data-testid={`cal-day-${c.dateStr}`}
              className={`relative aspect-square flex items-center justify-center text-[11px] rounded-md transition-colors ${
                hit ? "text-white font-semibold" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-[#1A2336]"
              } ${isToday && !hit ? "ring-2 ring-[#026FCB]" : ""}`}
              style={hit ? { backgroundColor: colorFor(first.id) } : {}}
              title={hit ? c.inTours.map((t) => t.name).join(" · ") : ""}
            >
              {c.day}
              {(c.inTours || []).length > 1 && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-white text-slate-800 rounded-full text-[9px] flex items-center justify-center font-bold">
                  {c.inTours.length}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default function ToursCalendar({ tours }) {
  const now = new Date();
  const [anchor, setAnchor] = useState({ year: now.getFullYear(), month: now.getMonth() });

  const months = [0, 1, 2].map((offset) => {
    const d = new Date(anchor.year, anchor.month + offset, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  const shift = (delta) => {
    const d = new Date(anchor.year, anchor.month + delta, 1);
    setAnchor({ year: d.getFullYear(), month: d.getMonth() });
  };

  const goToday = () => setAnchor({ year: now.getFullYear(), month: now.getMonth() });

  // Tours visible in the 3-month window, ordered chronologically
  const rangeStart = iso(months[0].year, months[0].month, 1);
  const rangeEndDate = new Date(months[2].year, months[2].month + 1, 0);
  const rangeEnd = iso(rangeEndDate.getFullYear(), rangeEndDate.getMonth(), rangeEndDate.getDate());
  const visibleTours = (tours || [])
    .filter((t) => t.start_date && t.end_date && !(t.end_date < rangeStart || t.start_date > rangeEnd))
    .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
  const colorByTourId = Object.fromEntries(
    visibleTours.map((t, i) => [t.id, TOUR_COLORS[i % TOUR_COLORS.length]])
  );
  const colorFor = (id) => colorByTourId[id] || "#94A3B8";

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2"><CalIcon size={18} />Calendario de expediciones</CardTitle>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid="cal-prev"><ChevronLeft size={14} /></Button>
          <Button variant="ghost" size="sm" onClick={goToday} data-testid="cal-today" className="text-xs">Hoy</Button>
          <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="cal-next"><ChevronRight size={14} /></Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-3 gap-6">
          {months.map((m) => (
            <MonthGrid key={`${m.year}-${m.month}`} year={m.year} month={m.month} tours={visibleTours} colorFor={colorFor} />
          ))}
        </div>
        {visibleTours.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100 dark:border-[#25334D] flex flex-wrap gap-2 text-xs">
            {visibleTours.map((t) => (
              <Link key={t.id} to={`/tours/${t.id}`} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-slate-200 dark:border-[#25334D] hover:bg-slate-50 dark:hover:bg-[#1A2336]">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colorFor(t.id) }} />
                <span className="truncate max-w-[140px]">{t.name}</span>
              </Link>
            ))}
          </div>
        )}
        {visibleTours.length === 0 && (
          <div className="mt-4 text-sm text-slate-500 text-center">Sin expediciones en este rango.</div>
        )}
      </CardContent>
    </Card>
  );
}
